import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { postNamedQuery } from '../../service'
import { useAppContext } from '../../redux/slices/appContext'

export const useSearchUserLoginActivity = (query = {}, options = {}) => {
  const { userId: caller } = useAppContext()
  return useQuery({
    queryKey: ['users.search-user-login-activity', caller, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('users', 'search-user-login-activity', query)
      return data
    },
    ...options
  })
}

export const useSearchUserLoginActivityAllAccess = (query = {}, options = {}) => {
  const { userId: caller } = useAppContext()
  return useQuery({
    queryKey: ['users.search-user-login-activity-all-access', caller, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('users', 'search-user-login-activity-all-access', query)
      return data
    },
    ...options
  })
}

/**
 * Rows + total for login activity grids (parallel details + resultType:'total' calls).
 * @param {{ query: object, allAccess: boolean, enabled?: boolean }} params
 */
export function useUserLoginActivity ({ query, allAccess, enabled = true, ...queryOptions }) {
  const queryDefaults = {
    keepPreviousData: true,
    ...queryOptions
  }

  const scopedRows = useSearchUserLoginActivity(query, {
    enabled: enabled && !allAccess,
    ...queryDefaults
  })
  const allAccessRows = useSearchUserLoginActivityAllAccess(query, {
    enabled: enabled && allAccess,
    ...queryDefaults
  })

  const countQuery = useMemo(() => ({ ...query, resultType: 'total' }), [query])

  const scopedCount = useSearchUserLoginActivity(countQuery, {
    enabled: enabled && !allAccess,
    ...queryDefaults
  })
  const allAccessCount = useSearchUserLoginActivityAllAccess(countQuery, {
    enabled: enabled && allAccess,
    ...queryDefaults
  })

  const rowsResult = allAccess ? allAccessRows : scopedRows
  const countResult = allAccess ? allAccessCount : scopedCount

  const rows = Array.isArray(rowsResult.data) ? rowsResult.data : []

  return {
    rows,
    total: countResult.data?.total ?? 0,
    isLoading: rowsResult.isLoading || countResult.isLoading,
    isFetching: rowsResult.isFetching || countResult.isFetching
  }
}
